import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Check, MagicWand } from "@phosphor-icons/react";

const initial = {
    owner: { full_name: "", national_id: "", phone: "", email: "", address: "", date_of_birth: "" },
    vehicle: { vin: "", make: "", model: "", year: 2024, color: "", body_type: "sedan", engine_number: "", fuel_type: "petrol", governorate: "A" },
    plate_number: "",
    plate_category: "private",
    documents: [],
    notes: "",
};

const REGIONS = [
    { code: "A" }, { code: "B" }, { code: "C" }, { code: "D" },
    { code: "E" }, { code: "F" }, { code: "G" }, { code: "H" },
];

export default function ApplicationNew() {
    const { t } = useI18n();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [data, setData] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [lpnSuggest, setLpnSuggest] = useState("");

    const set = (section, key, v) => setData((d) => ({ ...d, [section]: { ...d[section], [key]: v } }));

    // Auto-suggest next available LPN whenever region changes (or on mount).
    useEffect(() => {
        const region = data.vehicle.governorate;
        if (!region) return;
        api.get(`/lpn/next?region=${encodeURIComponent(region)}`)
            .then((r) => setLpnSuggest(r.data.suggested || ""))
            .catch(() => setLpnSuggest(""));
    }, [data.vehicle.governorate]);

    // Auto-accept the suggestion whenever the user hasn't typed anything yet.
    useEffect(() => {
        if (lpnSuggest && !data.plate_number) {
            setData((d) => ({ ...d, plate_number: lpnSuggest }));
        }
    }, [lpnSuggest]); // eslint-disable-line

    const submit = async () => {
        setBusy(true);
        try {
            const { data: res } = await api.post("/applications", data);
            toast.success(`${t("success")}: ${res.application.reference}`);
            nav(`/app/applications/${res.application.id}`);
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error"));
        } finally { setBusy(false); }
    };

    const steps = [t("stepOwner"), t("stepVehicle"), t("stepDocs"), t("stepReview")];

    const addFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setData((d) => ({
                ...d,
                documents: [...d.documents, { name: file.name, doc_type: "other", data_url: reader.result }],
            }));
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="max-w-4xl space-y-6" data-testid="application-new">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· NEW REGISTRATION</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("newApplication")}</h1>
            </div>

            <div className="flex gap-2">
                {steps.map((s, i) => (
                    <div key={i} className={`flex-1 h-1.5 rounded-sm ${i <= step ? "bg-primary" : "bg-secondary"}`} data-testid={`step-indicator-${i}`} />
                ))}
            </div>
            <div className="text-xs tracking-[0.25em] uppercase text-muted-foreground">{steps[step]}</div>

            <Card className="p-6 rounded-sm border-border">
                {step === 0 && (
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label={t("fullName")} value={data.owner.full_name} onChange={(v) => set("owner", "full_name", v)} testid="owner-name" />
                        <Field label={t("nationalId")} value={data.owner.national_id} onChange={(v) => set("owner", "national_id", v)} testid="owner-nid" />
                        <Field label={t("phone")} value={data.owner.phone} onChange={(v) => set("owner", "phone", v)} testid="owner-phone" />
                        <Field label={t("email")} type="email" value={data.owner.email} onChange={(v) => set("owner", "email", v)} testid="owner-email" />
                        <Field label={t("dob")} type="date" value={data.owner.date_of_birth} onChange={(v) => set("owner", "date_of_birth", v)} testid="owner-dob" />
                        <Field label={t("address")} value={data.owner.address} onChange={(v) => set("owner", "address", v)} testid="owner-address" />
                    </div>
                )}
                {step === 1 && (
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label={t("vin")} value={data.vehicle.vin} onChange={(v) => set("vehicle", "vin", v.toUpperCase())} testid="veh-vin" />
                        <Field label={t("make")} value={data.vehicle.make} onChange={(v) => set("vehicle", "make", v)} testid="veh-make" />
                        <Field label={t("model")} value={data.vehicle.model} onChange={(v) => set("vehicle", "model", v)} testid="veh-model" />
                        <Field label={t("year")} type="number" value={data.vehicle.year} onChange={(v) => set("vehicle", "year", Number(v))} testid="veh-year" />
                        <Field label={t("color")} value={data.vehicle.color} onChange={(v) => set("vehicle", "color", v)} testid="veh-color" />
                        <Field label={t("bodyType")} value={data.vehicle.body_type} onChange={(v) => set("vehicle", "body_type", v)} testid="veh-body" />
                        <Field label={t("engineNumber")} value={data.vehicle.engine_number} onChange={(v) => set("vehicle", "engine_number", v)} testid="veh-engine" />
                        <Field label={t("fuelType")} value={data.vehicle.fuel_type} onChange={(v) => set("vehicle", "fuel_type", v)} testid="veh-fuel" />
                        <div>
                            <Label className="text-xs tracking-widest uppercase font-bold">{t("region")}</Label>
                            <select
                                className="mt-1.5 h-10 w-full border border-input rounded-md bg-background px-2 text-sm"
                                value={data.vehicle.governorate}
                                onChange={(e) => set("vehicle", "governorate", e.target.value)}
                                data-testid="veh-governorate"
                            >
                                {REGIONS.map((g) => (
                                    <option key={g.code} value={g.code}>{t("region")} {g.code}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs tracking-widest uppercase font-bold">{t("plateNumber")} (LPN)</Label>
                            <div className="flex gap-2 mt-1.5">
                                <Input
                                    value={data.plate_number}
                                    onChange={(e) => setData((d) => ({ ...d, plate_number: e.target.value.toUpperCase() }))}
                                    placeholder={lpnSuggest || "A-0001"}
                                    className="mono"
                                    data-testid="app-lpn"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setData((d) => ({ ...d, plate_number: lpnSuggest }))}
                                    disabled={!lpnSuggest || data.plate_number === lpnSuggest}
                                    className="whitespace-nowrap"
                                    data-testid="app-lpn-suggest"
                                    title={t("lpnSuggestTip")}
                                >
                                    <MagicWand size={14} weight="bold" className="me-1" />{t("lpnUseSuggest")}
                                </Button>
                            </div>
                            {lpnSuggest && data.plate_number !== lpnSuggest && (
                                <div className="text-[11px] text-muted-foreground mt-1" data-testid="app-lpn-hint">
                                    {t("lpnNextAvailable")}: <span className="mono">{lpnSuggest}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs tracking-widest uppercase font-bold">{t("aluminumCategory")}</Label>
                            <select
                                className="mt-1.5 h-10 w-full border border-input rounded-md bg-background px-2 text-sm"
                                value={data.plate_category}
                                onChange={(e) => setData((d) => ({ ...d, plate_category: e.target.value }))}
                                data-testid="app-category"
                            >
                                <option value="private">{t("aluminumPrivate")}</option>
                                <option value="taxi">{t("aluminumTaxi")}</option>
                                <option value="temporary">{t("aluminumTemporary")}</option>
                                <option value="motorcycle">{t("aluminumMotorcycle")}</option>
                            </select>
                        </div>
                    </div>
                )}
                {step === 2 && (
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs tracking-widest uppercase font-bold">{t("addDocument")}</Label>
                            <Input type="file" accept="image/*,application/pdf" onChange={addFile} className="mt-1.5" data-testid="doc-file" />
                        </div>
                        <div className="space-y-2">
                            {data.documents.map((d, i) => (
                                <div key={i} className="flex items-center justify-between bg-secondary/50 px-3 py-2 rounded-sm text-sm">
                                    <span className="mono truncate max-w-md">{d.name}</span>
                                    <Button variant="ghost" size="sm" onClick={() => setData((x) => ({ ...x, documents: x.documents.filter((_, k) => k !== i) }))}>×</Button>
                                </div>
                            ))}
                        </div>
                        <div>
                            <Label className="text-xs tracking-widest uppercase font-bold">{t("notes")}</Label>
                            <Textarea value={data.notes} onChange={(e) => setData((d) => ({ ...d, notes: e.target.value }))} className="mt-1.5" data-testid="app-notes" />
                        </div>
                    </div>
                )}
                {step === 3 && (
                    <div className="space-y-4 mono text-sm">
                        <pre className="bg-secondary/40 p-4 rounded-sm overflow-auto max-h-96 whitespace-pre-wrap">
                            {JSON.stringify({ ...data, documents: data.documents.map((d) => ({ name: d.name, doc_type: d.doc_type })) }, null, 2)}
                        </pre>
                    </div>
                )}
            </Card>

            <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="wizard-back">
                    <CaretLeft size={16} className="me-1" />{t("back")}
                </Button>
                {step < 3 ? (
                    <Button onClick={() => setStep((s) => s + 1)} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="wizard-next">
                        {t("next")}<CaretRight size={16} className="ms-1" />
                    </Button>
                ) : (
                    <Button onClick={submit} disabled={busy} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="wizard-submit">
                        <Check size={16} weight="bold" className="me-1" />{busy ? "…" : t("submit")}
                    </Button>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <Label className="text-xs tracking-widest uppercase font-bold">{label}</Label>
            <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" data-testid={testid} />
        </div>
    );
}
